// @flow
import type { Dispatch } from "redux";
import type { RouterHistory } from "react-router-dom";
import { find } from "lodash";
import mediator from "@tvg/mediator";
import type { NullaryFn } from "@tvg/types/Functional";
import { openMyBetsStandalone } from "@tvg/sh-lib-my-bets/redux/actions";
import { openHandicappingStoreModal } from "@tvg/shared-actions/HandicappingStore";
import { onOpenComplianceModal } from "@tvg/sh-lib-account-wallet/src/utils/mobileUtils";
import {
  getAWPageTitle,
  AWPath
} from "@tvg/sh-lib-account-wallet/src/utils/AWPages";
import { openAccountWalletModal } from "@tvg/sh-lib-account-wallet/src/redux/actions";

import {
  openPromosModal,
  openBetPrefsModal,
  openNavigationModal,
  openFeatureTogglesModal,
  openTrackSelectorModal,
  openSupportModal,
  openWelcomeNewUserModal,
  openHelpModal,
  openPickBetsHowItWorksModal,
  openRaceSelectorModal
} from "../actions/modals";

export type Hash = {
  hash: string,
  call: NullaryFn<mixed>
};

export const Hashes = [
  {
    key: "bets",
    name: "Race Bets",
    hash: "#my-bets"
  },
  {
    key: "betPrefs",
    name: "BetPrefs",
    hash: "#bet-prefs"
  },
  {
    key: "promos",
    name: "Promos",
    hash: "#promos"
  },
  {
    key: "deposit",
    name: "Deposit",
    hash: "#deposit"
  },
  {
    key: "withdraw",
    name: "Withdraw",
    hash: "#withdraw"
  },
  {
    key: "privacyPolicy",
    name: "Privacy Policy",
    hash: "#privacyPolicy"
  },
  {
    key: "californiaPolicy",
    name: "California Privacy Policy",
    hash: "#state-specific-privacy-rights"
  },
  {
    key: "login",
    name: "Login",
    hash: "#login"
  },
  {
    key: "signUp",
    name: "Sign up",
    hash: "#sign-up"
  },
  {
    key: "featureToggles",
    name: "Feature Toggles",
    hash: "#features"
  },
  {
    key: "trackSelector",
    name: "Track Selector",
    hash: "#track-selector"
  },
  {
    key: "raceSelector",
    name: "Race Selector",
    hash: "#race-selector"
  },
  {
    key: "helpModalUnderstaningOdds",
    name: "Help Modal",
    hash: "#understandingOdds"
  },
  {
    key: "helpModalUnderstaningPools",
    name: "Help Modal",
    hash: "#understandingPools"
  },
  {
    key: "helpModalUnderstaningBetTypes",
    name: "Help Modal",
    hash: "#understandingBetTypes"
  },
  {
    key: "handicapStore",
    name: "Handicapping Store",
    hash: "#handicap-store"
  },
  {
    key: "support",
    name: "Support",
    hash: "#support"
  },
  {
    key: "welcome",
    name: "Welcome",
    hash: "#welcome"
  },
  {
    key: "pick-bets-how-it-works",
    name: "Pick Bets - How it works",
    hash: "#pick-bets-how-it-works"
  },
  {
    key: "accessibility",
    name: "Accessibility",
    hash: "#accessibility"
  },
  {
    key: "account-wallet",
    name: "Account and Wallet",
    hash: "#accountWallet"
  }
];

const defaultLoggedInCallback = (history: RouterHistory, location: string) => {
  history.push(location);
};

const defaultLoggedOutCallback = () => {
  mediator.base.dispatch({
    type: "OPEN_LOGIN"
  });
};

const openPrivateHash = (
  isLogged: boolean,
  loggedInCallback: NullaryFn<void>,
  loggedOutCallback: NullaryFn<void> = defaultLoggedOutCallback
) => {
  if (isLogged) {
    loggedInCallback();
  } else {
    loggedOutCallback();
  }
};

export const modalHashMap = (
  dispatch: Dispatch<*>,
  history: RouterHistory,
  location: string,
  isLogged: boolean,
  isAccountCompliant?: boolean
): Hash[] => {
  const loggedInCallback = () => defaultLoggedInCallback(history, location);
  return [
    {
      ...find(Hashes, (r) => r.key === "bets"),
      call: () =>
        openPrivateHash(isLogged, () => {
          if (!isAccountCompliant) {
            onOpenComplianceModal();
          } else {
            dispatch(openMyBetsStandalone());
          }
        })
    },
    {
      ...find(Hashes, (r) => r.key === "handicapStore"),
      call: () =>
        openPrivateHash(isLogged, () => {
          dispatch(openHandicappingStoreModal());
        })
    },
    {
      ...find(Hashes, (r) => r.key === "promos"),
      call: () => dispatch(openPromosModal())
    },
    {
      ...find(Hashes, (r) => r.key === "betPrefs"),
      call: () => dispatch(openBetPrefsModal())
    },
    {
      ...find(Hashes, (r) => r.key === "deposit"),
      call: () =>
        openPrivateHash(isLogged, () => {
          dispatch(
            openAccountWalletModal({
              path: AWPath.Deposit,
              title: getAWPageTitle(AWPath.Deposit)
            })
          );
        })
    },
    {
      ...find(Hashes, (r) => r.key === "withdraw"),
      call: () =>
        openPrivateHash(isLogged, () => {
          dispatch(
            openAccountWalletModal({
              path: AWPath.Withdrawals,
              title: getAWPageTitle(AWPath.Withdrawals)
            })
          );
        })
    },
    {
      ...find(Hashes, (r) => r.key === "featureToggles"),
      call: () => dispatch(openFeatureTogglesModal())
    },
    {
      ...find(Hashes, (r) => r.key === "trackSelector"),
      call: () => dispatch(openTrackSelectorModal())
    },
    {
      ...find(Hashes, (r) => r.key === "raceSelector"),
      call: () => dispatch(openRaceSelectorModal())
    },
    {
      ...find(Hashes, (r) => r.key === "helpModalUnderstaningOdds"),
      call: () => dispatch(openHelpModal())
    },
    {
      ...find(Hashes, (r) => r.key === "helpModalUnderstaningBetTypes"),
      call: () => dispatch(openHelpModal())
    },
    {
      ...find(Hashes, (r) => r.key === "helpModalUnderstaningPools"),
      call: () => dispatch(openHelpModal())
    },
    {
      ...find(Hashes, (r) => r.key === "login"),
      call: () =>
        openPrivateHash(isLogged, () => {
          if (!isAccountCompliant) {
            onOpenComplianceModal();
          } else {
            loggedInCallback();
          }
        })
    },
    {
      ...find(Hashes, (r) => r.key === "signUp"),
      call: () =>
        openPrivateHash(
          isLogged,
          () => {
            if (!isAccountCompliant) {
              onOpenComplianceModal();
            } else {
              loggedInCallback();
            }
          },
          () => {
            mediator.base.dispatch({
              type: "OPEN_SIGNUP"
            });
          }
        )
    },
    {
      ...find(Hashes, (r) => r.key === "privacyPolicy"),
      call: () => dispatch(openNavigationModal("privacy policy"))
    },
    {
      ...find(Hashes, (r) => r.key === "californiaPolicy"),
      call: () => dispatch(openNavigationModal("privacy policy"))
    },
    {
      ...find(Hashes, (r) => r.key === "support"),
      call: () => dispatch(openSupportModal())
    },
    {
      ...find(Hashes, (r) => r.key === "welcome"),
      call: () =>
        window.sessionStorage.getItem("newUser")
          ? dispatch(openWelcomeNewUserModal())
          : history.push("/")
    },
    {
      ...find(Hashes, (r) => r.key === "pick-bets-how-it-works"),
      call: () => dispatch(openPickBetsHowItWorksModal())
    },
    {
      ...find(Hashes, (r) => r.key === "accessibility"),
      call: () => dispatch(openNavigationModal("accessibility"))
    },
    {
      ...find(Hashes, (r) => r.key === "account-wallet"),
      call: () =>
        openPrivateHash(isLogged, () => {
          dispatch(
            openAccountWalletModal({
              title: "Account",
              path: "account"
            })
          );
        })
    }
  ];
};
